import { Capacitor } from '@capacitor/core';
import { cleanupLegacyLocalStorageKeys } from '@apexx/storage';
import { createApp } from 'vue';
import { installNativeConsoleSerialization } from './nativeConsoleSerialization';

// WebView → Logcat only sees strings; serialize objects before Cap bridges console.
installNativeConsoleSerialization();

cleanupLegacyLocalStorageKeys();

import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteLocationRaw } from 'vue-router';
import { routes, handleHotUpdate } from 'vue-router/auto-routes';

import {
	applyCachedAppThemeSync,
	beginKeycloakLogout,
	clearKeycloakInMemorySession,
	clearKeycloakSessionStorage,
	clientDebugLog,
	endKeycloakLogout,
	fcmToken,
	getKeycloak,
	isKeycloakLogoutInProgress,
	preloadClientDebugLogContext,
	setClientDebugLogSink,
	setFcmAccessTokenProvider,
	syncAppThemeFromAccessToken,
} from '@apexx/auth';

import { clearSentryUser, createSentryClientDebugSink, initSentry, setSentryUser } from '@apexx/common-composables';

import App from './App.vue';
import { rememberLastIdentityFromToken } from './auth/lastIdentity';
import {
	performHubUnauthorizedSession,
	resolveSignedOutRedirect,
} from './auth/logout';
import { bootstrapKeycloakAuth, loginWithKeycloak, setupKeycloakAuth } from './auth/setup';
import {
	bindPushNavigationHandler,
	resolvePostAuthPushDestination,
	resumePendingPushIfAny,
	setupPushNavigationListenerEarly,
} from './push/setupPushNavigation';
import { resumePendingShareIfAny, resolvePostAuthShareDestination, bindShareTargetHandler, setupShareTargetListenerEarly } from './share/setupShareTarget';
import { shellOrchestrator } from './shell/orchestrator';
import {
	isReturningToWelcomeFromHub,
	resolveDefaultHubAppDestination,
} from './shell/postAuthDestination';
import { markWelcomeSidebarAutoOpen } from './shell/welcomeSidebarAutoOpen';
import { normalizeHubTheme } from './shell/theme';
import { hubPinia as pinia } from './pinia';
import { useHubAuthStore } from './store/hubAuthStore';
import './styles/main.css';

import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { setUnresolvedBackFallback, useClose } from '@apexx/component-library';

// Load useClose module so Cap back-button listeners register.
void useClose;

defineCustomElements(window);
document.documentElement.dataset.hubShell = '';
normalizeHubTheme();
applyCachedAppThemeSync();
setupKeycloakAuth();

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
	scrollBehavior: () => ({ top: 0 }),
});

if (import.meta.hot) {
	handleHotUpdate(router);
}

router.afterEach(() => {
	normalizeHubTheme();
});

/** Sub-app primary landings (handlers decline) → welcome; hub welcome → exit toast. */
setUnresolvedBackFallback(() => {
	const route = router.currentRoute.value;
	if (route.name === 'app-shell') {
		void router.push({ name: 'launcher' });
		return true;
	}
	return false;
});

let resolveAuthBootstrap: (() => void) | undefined;
const authBootstrapReady = new Promise<void>((resolve) => {
	resolveAuthBootstrap = resolve;
});

/** Legacy machines auth URLs now live on the hub (`/`, `/login`). */
function resolveMachinesLegacyShellRedirect(
	to: RouteLocationNormalized,
	isLoggedIn: boolean
): RouteLocationRaw | undefined {
	if (to.name !== 'app-shell' || String(to.params.appId) !== 'machines') {
		return undefined;
	}

	const raw = to.params.pathMatch;
	const subPath = (Array.isArray(raw) ? raw.join('/') : String(raw ?? '')).replace(/^\/+|\/+$/g, '');
	const head = subPath.split('/')[0] ?? '';

	if (head !== 'login' && head !== 'intro') {
		return undefined;
	}

	if (!isLoggedIn) {
		return { name: 'login' };
	}

	// Let the machines sub-app pick the role-appropriate landing route (products for
	// potential clients, dashboard for staff, etc.).
	return { name: 'app-shell', params: { appId: 'machines' } };
}

router.beforeEach(async (to, from) => {
	await authBootstrapReady;

	const authStore = useHubAuthStore(pinia);

	const legacyMachinesRedirect = resolveMachinesLegacyShellRedirect(to, authStore.isLoggedIn);
	if (legacyMachinesRedirect) {
		return legacyMachinesRedirect;
	}
	const isPublic =
		to.meta?.public === true
		|| to.name === 'intro'
		|| to.name === 'login'
		|| to.name === 'login-redirect'
		|| to.name === 'login-otp'
		|| to.name === 'reauth';
	const isLoginRoute =
		to.name === 'login'
		|| to.name === 'login-redirect'
		|| to.name === 'login-otp'
		|| to.name === 'reauth';
	const requiresAuth = to.meta?.requiresAuth === true || to.name === 'launcher' || to.name === 'app-shell';

	if (isPublic) {
		if ((to.name === 'intro' || isLoginRoute) && authStore.isLoggedIn && !isKeycloakLogoutInProgress()) {
			// Prefer push (notification tap opened the app) over a pending share.
			const pushDestination = await resolvePostAuthPushDestination(authStore.accessToken);
			if (pushDestination) {
				return pushDestination;
			}

			const shareDestination = await resolvePostAuthShareDestination(authStore.accessToken);
			if (shareDestination) {
				return shareDestination;
			}

			return (
				(await resolveDefaultHubAppDestination(authStore.accessToken)) ?? {
					name: 'launcher',
				}
			);
		}

		return true;
	}

	if (requiresAuth && !authStore.isLoggedIn) {
		return resolveSignedOutRedirect('reauth');
	}

	if (to.name === 'launcher') {
		// Cold-start notification taps persist a destination before Vue mounts; honor it
		// when the hub would otherwise settle on the launcher.
		const pushDestination = await resolvePostAuthPushDestination(authStore.accessToken);
		if (pushDestination) {
			return pushDestination;
		}

		// Switch Apps → welcome must stay on the welcome page.
		if (isReturningToWelcomeFromHub(from.name)) {
			return true;
		}

		const defaultDestination = await resolveDefaultHubAppDestination(authStore.accessToken);
		if (defaultDestination) {
			return defaultDestination;
		}

		// Cold land on welcome (no last app) — open the app rail once for first-time pick.
		markWelcomeSidebarAutoOpen();
		return true;
	}

	return true;
});

function syncHubSentryUser(): void {
	const tokenParsed = getKeycloak().tokenParsed;
	if (!tokenParsed?.sub) {
		return;
	}

	setSentryUser({
		id: tokenParsed.sub,
		username: tokenParsed.preferred_username,
		email: tokenParsed.email,
	});
}

async function bootstrap(): Promise<void> {
	// Register Capgo / push tap listeners before auth so cold-start intents are queued.
	const shareListenerReady = setupShareTargetListenerEarly();
	const pushListenerReady = setupPushNavigationListenerEarly();

	const app = createApp(App);
	app.use(pinia);
	app.use(router);

	const authStore = useHubAuthStore(pinia);
	setFcmAccessTokenProvider(() => authStore.accessToken ?? null);

	initSentry({
		app,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		appName: 'hub',
		router,
		environment: import.meta.env.MODE,
		apiUrls: [
			import.meta.env.VITE_MACHINES_API_URL,
			import.meta.env.VITE_MACHINES_API_URL_DEMO,
			import.meta.env.VITE_CHAT_API_URL,
			import.meta.env.VITE_DOCUMENTS_API_URL,
			import.meta.env.VITE_TASKS_API_URL,
		],
		excludedUrls: [import.meta.env.VITE_KEYCLOAK_URL],
	});
	setClientDebugLogSink(createSentryClientDebugSink());
	await preloadClientDebugLogContext();

	try {
		await bootstrapKeycloakAuth(
			async (token) => {
				if (isKeycloakLogoutInProgress()) {
					return;
				}
				authStore.setAccessToken(token);
				syncHubSentryUser();
				await rememberLastIdentityFromToken(token);
				await syncAppThemeFromAccessToken(token);
				fcmToken.register().catch(() => {});
			},
			async () => {
				clientDebugLog('info', '[hub:logout] unauthenticated bootstrap path', {
					reason: 'keycloak-unauthenticated',
					endSsoSession: false,
				});
				authStore.clearAccessToken();
				clearSentryUser();
				await shellOrchestrator.unmountActive();

				const route = router.currentRoute.value;
				const isProtectedRoute =
					route.meta?.requiresAuth === true ||
					route.name === 'launcher' ||
					route.name === 'app-shell';

				if (isProtectedRoute) {
					await router.replace(await resolveSignedOutRedirect('reauth'));
				}

				normalizeHubTheme();
			},
			async () => {
				clientDebugLog('info', '[hub:logout] session expired (Keycloak refresh fatal)', {
					reason: 'session-expired',
					endSsoSession: false,
				});

				// Stay put on OTP / login / reauth screens — resume with no session must not
				// bounce mid-login back to the type picker.
				const route = router.currentRoute.value;
				const isLoginFlow =
					route.meta?.public === true
					|| route.name === 'login'
					|| route.name === 'login-redirect'
					|| route.name === 'login-otp'
					|| route.name === 'reauth'
					|| route.name === 'intro';

				if (isLoginFlow) {
					await beginKeycloakLogout();
					try {
						authStore.clearAccessToken();
						clearSentryUser();
						await shellOrchestrator.unmountActive().catch(() => {});
						clearKeycloakSessionStorage();
						clearKeycloakInMemorySession();
						normalizeHubTheme();
						authStore.clearAccessToken();
					} finally {
						endKeycloakLogout();
					}
					return;
				}

				await performHubUnauthorizedSession(router, 'session-expired');
			},
			async () => {
				clientDebugLog('info', '[hub:logout] native logout return deep link', {
					reason: 'native-logout-return',
					endSsoSession: false,
				});
				normalizeHubTheme();
				await shellOrchestrator.unmountActive();
				authStore.clearAccessToken();
				clearSentryUser();
				await router.replace(await resolveSignedOutRedirect('reauth'));
				normalizeHubTheme();
			},
		);
	} finally {
		resolveAuthBootstrap?.();
	}

	await shareListenerReady;
	await pushListenerReady;

	const getAuth = () => {
		const store = useHubAuthStore(pinia);
		return {
			isLoggedIn: store.isLoggedIn,
			accessToken: store.accessToken,
		};
	};

	await bindShareTargetHandler({
		router,
		getAuth,
	});

	if (authStore.isLoggedIn) {
		await resumePendingShareIfAny(router, authStore.accessToken);
	}

	app.mount('#app');
	await router.isReady();

	// Push tap navigation must run after mount. Capacitor re-delivers the cold-start
	// ActionPerformed when the early listener attaches (before Vue is mounted); replacing
	// the route then is overwritten when the WebView settles on `/`.
	await bindPushNavigationHandler({
		router,
		getAuth,
	});

	if (authStore.isLoggedIn) {
		await resumePendingPushIfAny(router, authStore.accessToken);
	}

	if (Capacitor.isNativePlatform()) {
		document.body.classList.add('native');
	}
}

void bootstrap();

export { loginWithKeycloak, router };
